/**
 * Utility functions for displaying Pokémon nature information
 */

/**
 * Gets a user-friendly description of how a nature affects stats
 * @param {string} nature - The nature name
 * @returns {Object} Object containing increased stat, decreased stat, and formatted description
 */
export function getNatureEffect(nature) {
  // Define nature effects based on the backend implementation
  const natureEffects = {
    // Positive Attack
    "Hardy": { increased: null, decreased: null },
    "Lonely": { increased: "atk", decreased: "def" },
    "Brave": { increased: "atk", decreased: "spe" },
    "Adamant": { increased: "atk", decreased: "spa" },
    "Naughty": { increased: "atk", decreased: "spd" },
    
    // Positive Defense
    "Bold": { increased: "def", decreased: "atk" },
    "Docile": { increased: null, decreased: null },
    "Relaxed": { increased: "def", decreased: "spe" },
    "Impish": { increased: "def", decreased: "spa" },
    "Lax": { increased: "def", decreased: "spd" },
    
    // Positive Speed
    "Timid": { increased: "spe", decreased: "atk" },
    "Hasty": { increased: "spe", decreased: "def" },
    "Serious": { increased: null, decreased: null },
    "Jolly": { increased: "spe", decreased: "spa" },
    "Naive": { increased: "spe", decreased: "spd" },
    
    // Positive Special Attack
    "Modest": { increased: "spa", decreased: "atk" },
    "Mild": { increased: "spa", decreased: "def" },
    "Quiet": { increased: "spa", decreased: "spe" },
    "Bashful": { increased: null, decreased: null },
    "Rash": { increased: "spa", decreased: "spd" },
    
    // Positive Special Defense
    "Calm": { increased: "spd", decreased: "atk" },
    "Gentle": { increased: "spd", decreased: "def" },
    "Sassy": { increased: "spd", decreased: "spe" },
    "Careful": { increased: "spd", decreased: "spa" },
    "Quirky": { increased: null, decreased: null }
  };

  // If nature isn't found or is null, return neutral effect
  if (!nature || !natureEffects[nature]) {
    return {
      increased: null,
      decreased: null,
      description: "Neutral (no effect on stats)"
    };
  }

  const effect = natureEffects[nature];
  
  // For neutral natures
  if (!effect.increased && !effect.decreased) {
    return {
      increased: null,
      decreased: null,
      description: "Neutral (no effect on stats)"
    };
  }

  // Map stat codes to readable names
  const statNames = {
    "atk": "Attack",
    "def": "Defense",
    "spa": "Sp. Attack",
    "spd": "Sp. Defense",
    "spe": "Speed"
  };

  return {
    increased: effect.increased,
    decreased: effect.decreased,
    description: `+10% ${statNames[effect.increased]}, -10% ${statNames[effect.decreased]}`
  };
}

/**
 * Get CSS class for a stat based on nature effect
 * @param {string} nature - The nature name
 * @param {string} stat - The stat code (atk, def, etc)
 * @returns {string} CSS class name
 */
export function getNatureStatClass(nature, stat) {
  if (!nature) return "";
  
  const effect = getNatureEffect(nature);
  
  if (effect.increased === stat) return "nature-boosted";
  if (effect.decreased === stat) return "nature-reduced";
  return "";
}
